/**
 * Keeper rules, in one place.
 *
 * These mirror `compute_keeper_values()` /`_compute_keeper_round()` in
 * WeezNFLData (`weez_nfl_data/models/services/keeper_service.py`). The backend is
 * the authority: it has already applied keeper_round_cost, the highest-round
 * block, the undrafted-round fallback and the max-years-kept limit before the
 * payload reaches us.
 *
 * This module exists because the rules used to be re-implemented separately in
 * KeeperTeamTile and KeeperTeamDetailModal. The two copies drifted -- the tile
 * was corrected, the modal was not -- and the modal ended up marking blocked
 * players "Eligible", marking keepable players "Ineligible", and pricing some
 * keepers with a sign-flipped, double-discounted formula. Keep the arithmetic
 * here and let the components render it.
 */

const asNumber = (value) => (typeof value === 'number' && !Number.isNaN(value) ? value : null);

/** ADP boards to consult on a `/api/players/` record, best first. */
const FANTASY_SCORING_TYPES = ['ppr', 'half_ppr', 'standard', 'non_ppr'];

/**
 * The round it COSTS to keep this player, or null if there is no such round.
 *
 * Passthrough by design: the backend already subtracted keeper_round_cost, so
 * subtracting it again here double-discounts every keeper.
 */
export const getKeeperRound = (player) => {
  const round = asNumber(player?.keeper_round);
  if (round !== null) {
    return round;
  }
  // Legacy shapes, kept for any cached payload predating the API field.
  return asNumber(player?.draft_round) ?? asNumber(player?.previous_year_draft_round);
};

/**
 * The round the player was ACTUALLY drafted in last season, before any keeper
 * discount. Null means they went undrafted, which is a meaningful answer -- it is
 * why they fall back to the league's undrafted keeper round.
 */
export const getLastYearDraftRound = (player) => asNumber(player?.last_year_draft_round);

/**
 * Whether a league rule blocks this player outright.
 *
 * `keeper_round === null` is the backend's signal that a rule fired (drafted too
 * early, or kept the maximum number of consecutive years); `note` carries which
 * one. Do NOT re-derive this from the keeper round: the highest-round block is a
 * rule about where a player was DRAFTED, not what he costs to keep, so a
 * round-5 pick who costs a 3rd is perfectly keepable.
 *
 * An absent keeper_round is deliberately NOT a block -- it means we have no
 * keeper row for this player at all (API failure, or a stale cached payload),
 * which is different from the backend having blocked him.
 */
export const isBlockedByRules = (player) => player?.keeper_round === null;

/** Human-readable reason a player is blocked, straight from the backend. */
export const getBlockedReason = (player) => (isBlockedByRules(player) ? player?.note || null : null);

/** Overall ADP rank, from the keeper payload first, then a `/api/players/` record. */
export const getADPRank = (player) => {
  const rank = asNumber(player?.adp_overall_rank);
  if (rank !== null) {
    return rank;
  }

  const fantasyData = player?.fantasy_data;
  if (fantasyData) {
    for (const type of FANTASY_SCORING_TYPES) {
      const adp = asNumber(fantasyData[type]?.adp_rank) ?? asNumber(fantasyData[type]?.ovr_rank);
      if (adp !== null) {
        return adp;
      }
    }
  }

  return null;
};

/**
 * The draft round the player's ADP implies.
 *
 * Prefers the backend's own `adp_round`, which was computed against the league's
 * real roster count -- recomputing from the overall rank falls back to a
 * hardcoded 12 teams and can disagree with the value the keeper was priced at.
 */
export const getADPRound = (player, teamCount = 12) => {
  const backendRound = asNumber(player?.adp_round);
  if (backendRound !== null) {
    return backendRound;
  }

  const rank = getADPRank(player);
  if (rank === null) {
    return null;
  }
  return Math.ceil(rank / (teamCount || 12));
};

/**
 * Surplus, in rounds: how much later than his market price you get to keep him.
 *
 * Positive is a bargain. Costing a 10th for a player going in round 5 is +5;
 * costing a 10th for a player going in round 18 is -8.
 *
 * Sign convention and arithmetic both match the backend's
 * `keeper_value = keeper_round - adp_round`. The old client-side fallback
 * computed `adp_round - (keeper_round - keeper_round_cost)`, which flipped the
 * sign AND re-applied a cost the backend had already taken off -- turning
 * Oronde Gadsden's -8 into a +10.
 */
export const getKeeperValue = (player, { teamCount = 12 } = {}) => {
  if (isBlockedByRules(player)) {
    return null;
  }

  const backendValue = asNumber(player?.keeper_value);
  if (backendValue !== null) {
    return backendValue;
  }

  // No backend value: it only omits one when it had no ADP row for the player.
  // `/api/players/` may still have an ADP board for him, so price it the same way.
  const keeperRound = getKeeperRound(player);
  const adpRound = getADPRound(player, teamCount);
  if (keeperRound === null || adpRound === null) {
    return null;
  }
  return keeperRound - adpRound;
};
